import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../lib/LanguageContext/LanguageContext.tsx";
import styles from "./HamburgerMenu.module.scss";

const HamburgerMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("header");
  const { t } = useLanguage();
  
  const navRef = useRef<HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false);
      
      // Update the URL hash without jumping
      window.history.pushState(null, "", `#${sectionId}`);
      
      // Accessibility: Move focus to the target section
      section.setAttribute('tabindex', '-1');
      section.focus({ preventScroll: true });
    }
  };

  // Track active section for aria-current
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = ["header", "experience", "skills", "contact"]
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Focus trap and Escape key handler
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      const nav = navRef.current;
      if (nav) {
        const focusableElements = nav.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Focus first element on open
        firstElement?.focus();

        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key === "Escape") {
            setIsOpen(false);
          } else if (e.key === "Tab") {
            if (e.shiftKey && document.activeElement === firstElement) {
              e.preventDefault();
              lastElement?.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              e.preventDefault();
              firstElement?.focus();
            }
          }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }
    } else if (previousFocusRef.current) {
      // Return focus to hamburger button when closed
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  return (
    <>
      <nav
        id="main-navigation"
        ref={navRef}
        className={`${styles.menu} ${isOpen ? styles.open : ""}`}
        aria-label="Main navigation"
        aria-hidden={!isOpen}
      >
        <ul role="list">
          <li>
            <button
              onClick={() => scrollToSection("header")}
              aria-label="Go to home section"
              aria-current={activeSection === "header" ? "true" : undefined}
            >
              {t("home")}
            </button>
          </li>
          <li>
            <button
              onClick={() => scrollToSection("experience")}
              aria-label="Go to experience section"
              aria-current={activeSection === "experience" ? "true" : undefined}
            >
              {t("experience")}
            </button>
          </li>
          <li>
            <button
              onClick={() => scrollToSection("skills")}
              aria-label="Go to skills section"
              aria-current={activeSection === "skills" ? "true" : undefined}
            >
              {t("skills")}
            </button>
          </li>
          <li>
            <button
              onClick={() => scrollToSection("contact")}
              aria-label="Go to contact section"
              aria-current={activeSection === "contact" ? "true" : undefined}
            >
              {t("contact")}
            </button>
          </li>
        </ul>
      </nav>
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={toggleMenu}
          role="presentation"
        ></div>
      )}
      <div className={`${styles.hamburgerContainer} hamburger-container`}>
        <button
          ref={buttonRef}
          className={`${styles.hamburger} ${isOpen ? styles.open : ""}`}
          onClick={toggleMenu}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isOpen}
          aria-controls="main-navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </>
  );
};

export default HamburgerMenu;
